import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import apiClient from '../lib/apiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SEOHead from '../components/SEOHead';
import { trackEvent } from '../components/Analytics';

const OrderSuccessPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { config } = useConfig();

  useEffect(() => {
    trackEvent('page_view', { page_title: 'Order Success' });
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await apiClient.getOrder(id);
        setOrder(response);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  if (loading || !config) {
    return <LoadingSpinner message="Loading order details..." />;
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <SEOHead title="Order Not Found" noindex={true} />
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h2>
          <Link to="/" className="inline-block bg-brand-blue hover:bg-brand-red text-white font-semibold px-6 py-3 rounded-lg transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 md:pt-24 pb-12 bg-gray-50">
      <SEOHead title="Order Confirmation" noindex={true} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">Order Placed Successfully!</h1>
          <p className="text-xl text-gray-600">Thank you for your order. We'll contact you soon to confirm delivery details.</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Order Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-lg">Order Information</h3>
              <div className="space-y-2">
                <p className="text-sm"><span className="font-medium">Order ID:</span> {order.id.substring(0, 8)}...</p>
                <p className="text-sm"><span className="font-medium">Status:</span> <span className="inline-block bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold capitalize">{order.status}</span></p>
                <p className="text-sm"><span className="font-medium">Total:</span> <span className="text-lg font-bold text-brand-blue">${order.total_amount}</span></p>
                <p className="text-sm"><span className="font-medium">Date:</span> {new Date(order.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-lg">Customer Information</h3>
              <div className="space-y-2">
                <p className="text-sm"><span className="font-medium">Name:</span> {order.customer_name}</p>
                <p className="text-sm"><span className="font-medium">Phone:</span> {order.customer_phone}</p>
                <p className="text-sm"><span className="font-medium">Email:</span> {order.customer_email}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4 text-lg">Delivery Address</h3>
            <p className="text-sm text-gray-700 leading-relaxed">{order.customer_address}</p>
            {order.delivery_preferences && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm">
                  <span className="font-medium text-brand-blue">Special Instructions:</span>
                  <span className="text-brand-red ml-2">{order.delivery_preferences}</span>
                </p>
              </div>
            )}
          </div>

          <div className="text-center space-x-4">
            <Link to="/products" className="inline-block bg-brand-blue hover:bg-brand-red text-white font-semibold px-8 py-3 rounded-lg transition-colors">
              Continue Shopping
            </Link>
            <Link to="/" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold px-8 py-3 rounded-lg transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;

